import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Unlock, AlertTriangle } from 'react-feather';
import { useApi } from '../../context/ApiContext';
import toast from 'react-hot-toast';
import styles from './RetrieveSecretPage.module.css';

const RetrieveSecretPage = () => {
  const { id } = useParams();
  const { getSecret, previewSecret } = useApi();

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [secret, setSecret] = useState(null);
  const [passphrase, setPassphrase] = useState('');

  // LOAD secret preview when component mounts
  useEffect(() => {
    loadPreview();
  }, [id]);

  // FETCH secret metadata without consuming
  const loadPreview = async () => {
    try {
      setLoading(true);
      const result = await previewSecret(id);
      setPreview(result);
    } catch (error) {
      toast.error(error.message);
      setPreview({ exists: false });
    } finally {
      setLoading(false);
    }
  };

  // RETRIEVE and consume the secret
  const handleRetrieveSecret = async () => {
    try {
      setLoading(true);
      const result = await getSecret(id, passphrase || null);
      setSecret(result);
      toast.success('Secret erfolgreich geöffnet!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // COPY secret content to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Secret in Zwischenablage kopiert!');
    } catch (error) {
      toast.error('Fehler beim Kopieren in die Zwischenablage');
    }
  };

  if (loading && !preview) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Secret wird geladen...</p>
        </div>
      </div>
    );
  }

  if (preview && (!preview.exists || preview.accessed)) {
    return (
      <div className="container">
        <div className={styles.error}>
          <h1>Secret {preview.accessed ? 'Bereits geöffnet' : 'Nicht gefunden'}</h1>
          <p>
            {preview.accessed 
              ? `Dieses Secret wurde am ${new Date(preview.consumed_at).toLocaleString()} geöffnet und wurde dauerhaft zerstört.`
              : 'Dieses Secret ist abgelaufen, wurde bereits geöffnet oder existiert nicht.'
            }
          </p>
        </div>
      </div>
    );
  }

  if (secret) {
    return (
      <div className="container">
        <div className={styles.success}>
          <h1><Unlock size={32} className={styles.successIcon} /> Secret geöffnet</h1>
          <p><strong style={{ color: 'var(--secondary-color)' }}>Achtung:</strong> Dieses Secret wurde dauerhaft zerstört und kann nicht erneut geöffnet werden.</p>

          <div className={styles.secretBox}>
            <label className="form-label">Inhalt:</label>
            <div className={styles.secretContent}>
              <textarea
                value={secret.secret}
                readOnly
                className="form-textarea"
                rows={6}
              />
              <button
                onClick={() => copyToClipboard(secret.secret)}
                className="btn btn-primary"
              >
                Kopieren
              </button>
            </div>
          </div>

          <div className={styles.warning}>
            <p><AlertTriangle size={16} className={styles.warningIcon} /> Dieses Secret wurde geöffnet und zerstört.</p>
            <p>Speichere es sicher, bevor du diese Seite verlässt.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>Secret öffnen</h1>

      {preview && (
        <div className={styles.preview}>
          <h2>Details</h2>
          <div className={styles.previewInfo}>
            <p><strong>Erstellt:</strong> {new Date(preview.created_at).toLocaleString()}</p>
            <p><strong>Ablauf:</strong> {new Date(preview.expires_at).toLocaleString()}</p>
            <p><strong>Gesichert:</strong> {preview.passphrase_required ? 'Ja (Passwort erforderlich)' : 'Nein'}</p>
          </div>

          {preview.passphrase_required && (
            <div className={styles.passphraseForm}>
              <div className="form-group">
                <label htmlFor="passphrase" className="form-label">
                  Passwort
                </label>
                <input
                  type="password"
                  id="passphrase"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  className="form-input"
                  placeholder="Gib das Passwort ein, um das Secret zu öffnen"
                />
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <button
              onClick={handleRetrieveSecret}
              disabled={loading || (preview.passphrase_required && !passphrase)}
              className="btn btn-danger"
            >
              {loading ? 'Öffnen...' : (
                <>
                  <Unlock size={18} className={styles.buttonIcon} /> Secret öffnen & zerstören
                </>
              )}
            </button>
          </div>

          <div className={styles.warning}>
            <p><strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden!</p>
            <p>Das Secret wird dauerhaft zerstört, nachdem es geöffnet wurde.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetrieveSecretPage;